import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Alien2048NewLandingProps {
  twitterUser?: unknown;
  onTwitterSignIn?: () => void;
  onTwitterSignOut?: () => void;
}

/**
 * Generate a random display name for anonymous players.
 */
function generateRandomName(): string {
  const adjectives = ['Cosmic', 'Stellar', 'Astral', 'Lunar', 'Solar', 'Nebula'];
  const nouns = ['Voyager', 'Explorer', 'Pilot', 'Captain', 'Navigator', 'Ranger'];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const num = Math.floor(Math.random() * 999);
  return `${adj}${noun}${num}`;
}

/**
 * Gear icon for settings.
 */
const GearIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
  </svg>
);

/**
 * New Alien 2048 landing page.
 */
function Alien2048NewLanding(_props: Alien2048NewLandingProps) {
  const navigate = useNavigate();

  const handlePlay = () => {
    const playerName = generateRandomName();
    sessionStorage.setItem('alien2048_playerName', playerName);
    navigate('/alien2048/play');
  };

  const handleLeaderboard = () => {
    navigate('/alien2048/leaderboard');
  };

  const handleSettings = () => {
    navigate('/alien2048/settings');
  };

  return (
    <div style={styles.container}>
      {/* Banner */}
      <div style={styles.bannerContainer}>
        <img
          src="/images/alien2048banner_small.png"
          alt="Alien 2048"
          style={styles.bannerImage}
        />
      </div>

      {/* Tagline */}
      <div style={styles.tagline}>Swipe to move tiles and combine matching aliens</div>

      {/* Action Buttons Row */}
      <div style={styles.buttonRow}>
        <button style={styles.playButton} onClick={handlePlay}>
          Play
        </button>
        <button style={styles.leaderboardButton} onClick={handleLeaderboard}>
          Leaderboard
        </button>
        <button style={styles.settingsButton} onClick={handleSettings} title="Settings">
          <GearIcon />
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#FEAC64', // Orange theme like mfers arcade
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
  },
  bannerContainer: {
    width: '100%',
    maxWidth: '400px',
    marginTop: '20px',
    marginBottom: '16px',
    zIndex: 1,
  },
  bannerImage: {
    width: '100%',
    height: 'auto',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
  tagline: {
    fontSize: '16px',
    color: '#333',
    marginBottom: '24px',
    zIndex: 1,
    textAlign: 'center',
  },
  buttonRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    alignItems: 'center',
    zIndex: 1,
  },
  playButton: {
    backgroundColor: '#1e8449', // Green like mfers arcade
    color: '#fff',
    border: '2px solid #333',
    borderRadius: '8px',
    padding: '12px 36px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    height: '48px',
    boxSizing: 'border-box',
  },
  leaderboardButton: {
    backgroundColor: '#9b59b6', // Purple
    color: '#fff',
    border: '2px solid #333',
    borderRadius: '8px',
    padding: '12px 18px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    height: '48px',
    boxSizing: 'border-box',
  },
  settingsButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    border: '2px solid #333',
    borderRadius: '8px',
    width: '48px',
    height: '48px',
    cursor: 'pointer',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  authSection: {
    marginBottom: '20px',
    zIndex: 1,
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    borderRadius: '12px',
    padding: '12px 16px',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#333',
  },
  userName: {
    fontWeight: 'bold',
    fontSize: '14px',
  },
  logoutButton: {
    backgroundColor: 'transparent',
    color: '#333',
    border: '2px solid #333',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  signInButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#000',
    color: '#fff',
    border: '2px solid #333',
    borderRadius: '8px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  modal: {
    backgroundColor: '#FEAC64',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '300px',
    width: '90%',
    textAlign: 'center',
    border: '3px solid #333',
  },
  modalTitle: {
    margin: '0 0 12px 0',
    fontSize: '18px',
    color: '#333',
  },
  modalText: {
    margin: '0 0 20px 0',
    fontSize: '14px',
    color: '#555',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  modalCancelButton: {
    backgroundColor: '#1e8449',
    color: '#fff',
    border: '2px solid #333',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  modalConfirmButton: {
    backgroundColor: '#c0392b',
    color: '#fff',
    border: '2px solid #333',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
};

export default Alien2048NewLanding;
